import { AlternateEmail, DevicesOther, Logout, Password, SwitchAccount, VpnKey, Payments as PaymentsIcon } from "@mui/icons-material";
import { Box, Card, CardContent, Chip, Grid, List, ListItemAvatar, ListItemButton, ListItemText, Paper, Typography } from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";
import { useAuth, useConfig } from "../allauth/auth";
import { useAppContext } from "../context/AppContext";
import ProfileIcon from "../theme/icons/ProfileIcon";
import ThemeSwitcher from "../theme/ThemeSwitcher";
import { getRoleConfig } from "../utils/roleUtils";

interface PermissionProps {
    to: string;
    icon?: string | React.ReactNode;
    name?: string;
}

const NavBarItem: React.FC<PermissionProps> = (props) => {
    return props.to.indexOf("http://") === 0 ||
        props.to.indexOf("https://") === 0 ? (
        <ListItemButton dense={true} alignItems={"center"}>
            {props.icon && (
                <ListItemAvatar sx={{ minWidth: 40 }}>{props.icon}</ListItemAvatar>
            )}
            <a
                target={"_blank"}
                style={{ textDecoration: "none", fontSize: 12 }}
                href={props.to}
            >
                {props.name}
            </a>
        </ListItemButton>
    ) : (
        <ListItemButton
            dense={true}
            component={Link}
            to={props.to}
            alignItems={"center"}
        >
            {props.icon && (
                <ListItemAvatar sx={{ minWidth: 40 }}>{props.icon}</ListItemAvatar>
            )}
            <ListItemText primary={props.name} />
        </ListItemButton>
    );
};

export default function MyAccount() {
    const me = useAuth()?.data?.user;
    const config = useConfig();
    const { membership } = useAppContext();
    const availableRoles = membership?.roles || [];
    const primaryRole = availableRoles.length > 0 ? availableRoles[0] : null;

    if (!me || !me?.id) {
        return (
            <Box sx={{ mt: 3 }} alignItems="center" justifyContent="center">
                <Typography variant="h4" gutterBottom textAlign="center">
                    My Profile
                </Typography>
                <Typography textAlign="center">Please sign in to view your profile.</Typography>
            </Box>
        );
    }

    return (
        <Box p={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
                <Typography variant="h4">My Profile</Typography>
                {availableRoles.length > 0 && (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {availableRoles.map((role) => (
                            <Chip
                                key={role}
                                size="small"
                                label={getRoleConfig(role).label}
                                color={getRoleConfig(role).color}
                            />
                        ))}
                    </Box>
                )}
            </Box>

            {/* Account Management Section */}
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Account Management
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <List dense>
                                <NavBarItem
                                    to={`/dashboard`}
                                    icon={<ProfileIcon fontSize={"small"} />}
                                    name="My Dashboard"
                                />
                                <NavBarItem
                                    to={`/account/payments`}
                                    icon={<PaymentsIcon fontSize={"small"} />}
                                    name="My Payments"
                                />
                                <NavBarItem
                                    to="/account/email"
                                    icon={<AlternateEmail fontSize={"small"} />}
                                    name="Change Email"
                                />
                                <NavBarItem
                                    to="/account/password/change"
                                    icon={<Password fontSize={"small"} />}
                                    name="Change Password"
                                />
                                {config.data.socialaccount ? (
                                    <NavBarItem
                                        to="/account/providers"
                                        icon={<SwitchAccount fontSize={"small"} />}
                                        name="Providers"
                                    />
                                ) : null}
                            </List>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <List dense>
                                {config.data.mfa ? (
                                    <NavBarItem
                                        to="/account/2fa"
                                        icon={<VpnKey fontSize={"small"} />}
                                        name="Two-Factor Authentication"
                                    />
                                ) : null}
                                {config.data.usersessions ? (
                                    <NavBarItem
                                        to="/account/sessions"
                                        icon={<DevicesOther fontSize={"small"} />}
                                        name="Sessions"
                                    />
                                ) : null}
                                <NavBarItem
                                    to="/account/logout"
                                    icon={<Logout fontSize={"small"} />}
                                    name="Sign Out"
                                />
                            </List>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Theme and Font Controls */}
            <Paper sx={{ mt: 3, p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Appearance Settings
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <ThemeSwitcher />
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
}
